import { useEffect, useState } from "react";
import RightTimeApi from "@/API";
import { appendBillingOrgQuery } from "@/utils/billingOrgHelpers";
import Helpers from "@/Helpers";
import { toast } from "react-toastify";

export default function PendingApprovalInvoicesTab({ isActive, onInvoiceClick, formatCurrency, updateSpinner, refreshKey, onDataChanged, billingOrgId = 0 }) {
  const [pendingInvoices, setPendingInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [approvingAll, setApprovingAll] = useState(false);

  useEffect(() => {
    if (isActive) fetchPendingInvoices();
  }, [isActive, refreshKey, billingOrgId]);

  async function fetchPendingInvoices() {
    setLoading(true);
    setError(null);
    updateSpinner && updateSpinner(true);
    try {
      const resp = await RightTimeApi.getAsync(appendBillingOrgQuery("/Invoices/PendingApproval", billingOrgId));
      if (resp && resp.ok && resp.jsonData) {
        setPendingInvoices(resp.jsonData);
      } else {
        setError("Failed to load pending approval invoices");
      }
    } catch (err) {
      setError("Error loading pending approval invoices: " + err.message);
    } finally {
      setLoading(false);
      updateSpinner && updateSpinner(false);
    }
  }

  async function handleApproveAll() {
    if (!window.confirm("Approve all pending invoices you are authorized to approve?")) {
      return;
    }

    setApprovingAll(true);
    updateSpinner && updateSpinner(true);
    try {
      const resp = await RightTimeApi.postAsync("/Invoices/ApproveAll", {});
      if (resp?.ok) {
        const approvedCount = resp.jsonData?.approvedCount ?? 0;
        const skippedCount = resp.jsonData?.skippedCount ?? 0;
        toast.success(`Approved ${approvedCount} invoice(s)${skippedCount ? ` (${skippedCount} skipped)` : ""}`);
        await fetchPendingInvoices();
        onDataChanged && onDataChanged();
      } else {
        toast.error("Failed to approve invoices");
      }
    } catch (err) {
      toast.error("Error approving invoices: " + err.message);
    } finally {
      setApprovingAll(false);
      updateSpinner && updateSpinner(false);
    }
  }

  return (
    <div className="horizontal">
      <div className="tableWrapper dimensionTable scrollable collapsable collapsed">
        <table>
          <caption>
            <span className="title">Invoices Pending Approval</span>{pendingInvoices ? (
              <>
                <span className="captionCount">({pendingInvoices.length.toString()})</span>
                <span className="captionRight">
                  {pendingInvoices.length > 0 && (
                    <button
                      type="button"
                      className="action generate"
                      disabled={approvingAll}
                      onClick={handleApproveAll}
                    >
                      Approve All
                    </button>
                  )}
                </span>
              </>
            ) : null}
          </caption>
          <thead>
            <tr>
              <th>Billing Org</th>
              <th>Company</th>
              <th>Period Start</th>
              <th>Period End</th>
              <th>Amount</th>
              <th>Item Count</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pendingInvoices.length > 0 ? (
              pendingInvoices.map((invoice) => (
                <tr key={invoice.id}>
                  <td>{invoice.billingOrgName || ""}</td>
                  <td>{invoice.companyName}</td>
                  <td>{Helpers.formatDate(invoice.periodStart)}</td>
                  <td>{Helpers.formatDate(invoice.periodEnd)}</td>
                  <td className="right">{formatCurrency(invoice.currentAmount)}</td>
                  <td className="center">{invoice.itemCount}</td>
                  <td className="actions">
                    <button
                      onClick={() => onInvoiceClick(invoice)}
                      className="action preview"
                      title="Review Invoice"
                    >
                      Review
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} className="center">No invoices pending approval</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
